import React, { useContext, useState } from "react";
import t from "Utils/translation";
import s from "../../delivery.scss";
import ResponsiveTable from "Components/responsiveTable/ResponsiveTable";
import Button from "Components/button/Button";
import useSWR from "swr";
import WarningBanner from "Components/warningBanner/WarningBanner";
import SmallModal from "Components/smallModal/smallModal";
import { deliveryPlans } from "@/config";
import { useHistory, useParams } from "react-router-dom";
import DashboardAnalytics from "Components/dashboardAnalytics/DashboardAnalytics";
import { AuthContext, MediaQueriesContext } from "@/App";
import LoaderIcon from "Components/loader/LoaderIcon";
import QboxmailApi from "@/qboxmailApi";
import ConfirmModal from "Components/confirmModal/ConfirmModal";
import cx from "classnames";

const api = new QboxmailApi({ useCookie: true });

const formatter = new Intl.NumberFormat(navigator.language || navigator.userLanguage, {
  maximumFractionDigits: 0,
});

const DashboardTable = ({ tableData }) => (
  <div>
    {tableData.map((item) => (
      <div key={item.name} className={s.accountInfoRow}>
        <div className={s.accountInfoRowItem}>{item.name}</div>
        <div className={s.accountInfoRowItem}>{item.value}</div>
      </div>
    ))}
  </div>
);

const ActivityDashboard = ({ analytics }) => {
  const { isMediaMobile } = useContext(MediaQueriesContext);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-center">
        <DashboardAnalytics analyticsData={analytics} isSmall={isMediaMobile} />
      </div>
      {/* <div className="flex flex-col mt-10">
        <div className="w-full flex justify-between text-sm border-0 border-b border-solid border-[--bg-d5] py-3">
          <div className="flex">
            <div>Bounce rate</div>
            <div className="ml-5">13%</div>
          </div>
          <div className="flex">
            <div>Reputazione</div>
            <div className="ml-5">Bassa</div>
          </div>
          <div className="flex">
            <Icon svg={warningFill} />
          </div>
        </div>
        <div className="mt-2 text-xs text-qb-text-a40">Un Bounce rate del 15% blocca l&apos;utenza</div>
      </div> */}
    </div>
  );
};

const PlanUpdateSection = ({
  hasPendingOrder,
  planUpdateMsg,
  orderCancelModalTitle,
  orderCancelModalContent,
  onOrderCancel = () => {},
  onPlanUpdate = () => {},
  buttonComponent,
  isLoading = false,
  accountCode,
  history,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  return (
    <div className="flex flex-col items-end justify-end mt-6 min-h-[64px]">
      {isLoading && <LoaderIcon className="mt-6" color="var(--bg-d10)" />}
      {!isLoading && hasPendingOrder ? (
        <>
          <WarningBanner color="blue" msg={planUpdateMsg} />
          <div className="mt-2 text-[--accent] text-sm cursor-pointer" onClick={() => setIsModalOpen(true)}>
            {t("button.modifyPlan.cancel")}
          </div>

          <SmallModal
            closeModal={() => setIsModalOpen(false)}
            modalClassName=""
            title={orderCancelModalTitle}
            opened={isModalOpen}
            showCloseIcon
          >
            <div className="section">{orderCancelModalContent}</div>
            <div className="mb-4">
              <Button
                className="self-end"
                type="button"
                color="red"
                onClick={async () => {
                  setIsCancelling(true);
                  await onOrderCancel();
                  setIsCancelling(false);
                  setIsModalOpen(false);
                }}
                loading={isCancelling}
              >
                button.order.cancel
              </Button>
            </div>
          </SmallModal>
        </>
      ) : (
        !isLoading && (buttonComponent || <Button onClick={onPlanUpdate}>button.moreMessages</Button>)
      )}
    </div>
  );
};

const FreePlanDetails = ({
  accountData,
  onPlanUpdate,
  pendingOrders,
  reloadPendingOrders,
  isValidatingPendingOrders,
  history,
  isLoggedUserAdmin,
}) => (
  <div className="flex-[2]">
    <div className="settingTitle">{t("dashboard.planDetails.free.content.title")}</div>
    <DashboardTable
      tableData={[
        {
          name: t("detailInfo.label.planVolumes"),
          value: t("detailInfo.data.volume.monthly", null, { amount: formatter.format(accountData.num_messages) }),
        },
        { name: t("detailInfo.label.remainingMessages"), value: formatter.format(accountData.remaining_messages) },
        {
          name: t("detailInfo.label.connectedDomains"),
          value: accountData.domains_count ? accountData.domains_count : 0,
        },
      ]}
    />

    {!isLoggedUserAdmin && (
      <PlanUpdateSection
        onOrderCancel={async () => {
          try {
            await api.cancelDeliveryPendingOrder(accountData.id);
            await reloadPendingOrders();
          } catch (err) {}

          return true;
        }}
        hasPendingOrder={pendingOrders?.plan?.length > 0}
        planUpdateMsg={t("infoMsg.delivery.pendingUpdateOrder.modification")}
        orderCancelModalTitle={t("delivery.modal.planModification.cancel.title")}
        orderCancelModalContent={t("delivery.modal.planModification.cancel.content")}
        onPlanUpdate={onPlanUpdate}
        isLoading={!pendingOrders || isValidatingPendingOrders}
        accountCode={accountData.id}
        history={history}
        buttonComponent={<Button onClick={onPlanUpdate}>button.buyPlan</Button>}
      />
    )}
  </div>
);

const MonthlyPlanDetails = ({
  accountData,
  onPlanUpdate,
  pendingOrders,
  reloadPendingOrders,
  isLoggedUserAdmin,
  isValidatingPendingOrders,
}) => (
  <div className="flex-[2]">
    <div className="settingTitle">{t("dashboard.planDetails.monthly.content.title")}</div>
    <DashboardTable
      tableData={[
        {
          name: t("detailInfo.label.planVolumes"),
          value: t("detailInfo.data.volume.monthly", null, { amount: formatter.format(accountData.num_messages) }),
        },
        { name: t("detailInfo.label.remainingMessages"), value: formatter.format(accountData.remaining_messages) },
        {
          name: t("detailInfo.label.connectedDomains"),
          value: accountData.domains_count ? accountData.domains_count : 0,
        },
      ]}
    />

    {accountData.status_detail !== "wait_pay" && !isLoggedUserAdmin && (
      <PlanUpdateSection
        onOrderCancel={async () => {
          try {
            await api.cancelDeliveryPendingOrder(accountData.id);
            await reloadPendingOrders();
          } catch (err) {}

          return true;
        }}
        hasPendingOrder={pendingOrders?.plan?.length > 0 || pendingOrders?.extra?.length > 0}
        planUpdateMsg={
          pendingOrders?.extra?.length > 0
            ? t("infoMsg.delivery.pendingExtradeliveryOrder")
            : t("infoMsg.delivery.pendingUpdateOrder.modification")
        }
        orderCancelModalTitle={t("delivery.modal.planModification.cancel.title")}
        orderCancelModalContent={t("delivery.modal.planModification.cancel.content")}
        onPlanUpdate={onPlanUpdate}
        buttonComponent={
          pendingOrders?.plan?.length === 0 ? (
            <div
              className={cx("text-right text-[--accent] text-sm cursor-pointer", {
                ["opacity-50 pointer-events-none"]: !!accountData.delete_on,
              })}
              onClick={onPlanUpdate}
            >
              {t("button.modifyPlan.monthly")}
            </div>
          ) : (
            <div></div>
          )
        }
        isValidatingPedingOrders={isValidatingPendingOrders}
      />
    )}
  </div>
);

const PrepaidPlanDetails = ({
  accountData,
  onPlanUpdate,
  pendingOrders,
  reloadPendingOrders,
  isValidatingPedingOrders,
  isLoggedUserAdmin,
}) => (
  <div className="flex-[2]">
    <div className="settingTitle">{t("dashboard.planDetails.prepaid.content.title")}</div>
    <DashboardTable
      tableData={[
        {
          name: t("detailInfo.label.planVolumes"),
          value: t("detailInfo.data.volume.prepaid", null, { amount: formatter.format(accountData.num_messages) }),
        },
        { name: t("detailInfo.label.remainingMessages"), value: formatter.format(accountData.remaining_messages) }, // TODO: don't show extra in this total
        {
          name: t("detailInfo.label.connectedDomains"),
          value: accountData.domains_count ? accountData.domains_count : 0,
        },
        { name: t("detailInfo.label.expirationDate"), value: accountData.plan_expiration_date }, // TODO: don't show extra in this total
      ]}
    />

    {accountData.status_detail !== "wait_pay" && !isLoggedUserAdmin && (
      <PlanUpdateSection
        onOrderCancel={async () => {
          try {
            await api.cancelDeliveryPendingOrder(accountData.id);
            await reloadPendingOrders();
          } catch (err) {}

          return true;
        }}
        buttonComponent={
          pendingOrders?.plan?.length === 0 ? (
            <div
              className={cx("text-right text-[--accent] text-sm cursor-pointer", {
                ["opacity-50 pointer-events-none"]: !!accountData.delete_on,
              })}
              onClick={onPlanUpdate}
            >
              {t("delivery.accounts.update.stepPlanModification.title.prepaid")}
            </div>
          ) : (
            <div></div>
          )
        }
        hasPendingOrder={pendingOrders?.plan?.length > 0}
        planUpdateMsg={t("infoMsg.delivery.pendingUpdateOrder.purchase")}
        orderCancelModalTitle={t("delivery.modal.planModification.cancel.title")}
        orderCancelModalContent={t("delivery.modal.planModification.cancel.content")}
        onPlanUpdate={onPlanUpdate}
        isValidatingPedingOrders={isValidatingPedingOrders}
      />
    )}
  </div>
);

const Dashboard = ({ accountData }) => {
  const history = useHistory();

  const { customerCode } = useParams();

  const { isLoggedUserAdmin, getLoggedUser } = useContext(AuthContext);
  const [isForbiddenModalOpen, setIsForbiddenModalOpen] = useState(false);

  const {
    data: pendingOrders,
    isValidating: isValidatingPedingOrders, //tmp solution
    mutate: reloadPendingOrders,
  } = useSWR(`/delivery/accounts/${accountData.id}/pending_orders`);

  const { data: domainsRank, isValidating: isLoadingDomainsRank } = useSWR(
    `/delivery/tracemail/daily/${accountData.id}?type=domains-rank`,
  );

  const { data: dailyBreakdown, isValidating: isLoadingDailyBreakdown } = useSWR(
    `/delivery/tracemail/daily/${accountData.id}`,
  );

  let baseUrl = "";
  if (isLoggedUserAdmin()) {
    baseUrl = "/customers/" + customerCode;
  }

  const loggedUser = getLoggedUser();

  // if is admin we get the limits later in the wizard, once we know the code
  const { data: limits } = useSWR(!isLoggedUserAdmin() && `/users/delivery_limits`, null, {
    revalidateOnFocus: false,
  });

  const canBuyExtraMessages = !(
    loggedUser.type === "TeamMember" && !loggedUser.permissions.includes("buy_extra_messages")
  );

  const canEditAccountPlan = !(
    loggedUser.type === "TeamMember" && !loggedUser.permissions.includes("edit_account_plan")
  );

  const goToUpdate = () => {
    if (!canEditAccountPlan) {
      setIsForbiddenModalOpen(true);
      return;
    }

    history.push(`/delivery/accounts/update/${accountData.id}`);
  };

  const goToChangePlan = () => {
    if (!canEditAccountPlan) {
      setIsForbiddenModalOpen(true);
      return;
    }

    history.push(`/delivery/accounts/change-plan/${accountData.id}`);
  };

  const goToExtradelivery = () => {
    if (!canBuyExtraMessages) {
      setIsForbiddenModalOpen(true);
      return;
    }

    history.push(`/delivery/accounts/extra-delivery/${accountData.id}`);
  };

  return (
    <div className={s.dashboardContainer}>
      <div className={s.dashboardContent}>
        <div className="flex-[1]">
          <div className="settingTitle">{t("delivery.accounts.dashboard.details.title")}</div>
          <div className="sectionDesc">{t("delivery.accounts.dashboard.details.desc")}</div>
        </div>
        {accountData.plan === deliveryPlans.FREE && (
          <FreePlanDetails
            reloadPendingOrders={reloadPendingOrders}
            accountData={accountData}
            onPlanUpdate={goToChangePlan}
            isLoggedUserAdmin={isLoggedUserAdmin()}
            pendingOrders={pendingOrders}
            isValidatingPendingOrders={isValidatingPedingOrders}
            history={history}
          />
        )}
        {accountData.plan === deliveryPlans.MONTHLY && (
          <MonthlyPlanDetails
            reloadPendingOrders={reloadPendingOrders}
            accountData={accountData}
            isLoggedUserAdmin={isLoggedUserAdmin()}
            onPlanUpdate={goToUpdate}
            pendingOrders={pendingOrders}
          />
        )}
        {accountData.plan === deliveryPlans.PREPAID && (
          <PrepaidPlanDetails
            reloadPendingOrders={reloadPendingOrders}
            accountData={accountData}
            isLoggedUserAdmin={isLoggedUserAdmin()}
            onPlanUpdate={goToUpdate}
            pendingOrders={pendingOrders}
          />
        )}
      </div>
      {accountData.plan === deliveryPlans.MONTHLY && (
        <div className={s.dashboardContent}>
          <div className="flex-[1]">
            <div className="settingTitle">{t("dashboard.extraDelivery.title")}</div>
            <div className="sectionDesc">{t("dashboard.extraDelivery.desc")}</div>
          </div>
          <div className="flex-[2]">
            <DashboardTable
              tableData={[
                {
                  name: t("detailInfo.label.availableMessages"),
                  value: accountData.extra_messages_expires_at ? accountData.remaining_extra_messages_bought : "-",
                },
                // { name: t("detailInfo.label.expirationDate"), value: accountData.extra_messages_expires_at || "-" },
              ]}
            />

            {accountData.status_detail !== "wait_pay" && (
              <PlanUpdateSection
                hasPendingOrder={pendingOrders?.extra?.length > 0}
                onOrderCancel={async () => {
                  try {
                    await api.cancelDeliveryPendingOrder(accountData.id);
                    await reloadPendingOrders();
                  } catch (err) {}

                  return true;
                }}
                planUpdateMsg={t("infoMsg.delivery.pendingExtradeliveryOrder")}
                orderCancelModalTitle={t("delivery.modal.extradeliveryOrder.cancel.title")}
                orderCancelModalContent={t("delivery.modal.extradeliveryOrder.cancel.content")}
                buttonComponent={
                  isValidatingPedingOrders ? (
                    <LoaderIcon className="mt-6" color="var(--bg-d10)" />
                  ) : !isValidatingPedingOrders && pendingOrders?.extra?.length === 0 ? (
                    <div
                      className={cx("mt-6 text-right text-[--accent] text-sm cursor-pointer", {
                        ["opacity-50 pointer-events-none"]: pendingOrders?.plan?.length > 0 || !!accountData.delete_on,
                      })}
                      onClick={goToExtradelivery}
                    >
                      {t("button.buyExtraDelivery")}
                    </div>
                  ) : (
                    <div></div>
                  )
                }
              />
            )}
          </div>
        </div>
      )}
      <div className={s.dashboardContent}>
        <div className="flex-[1]">
          <div className="settingTitle">{t("delivery.accounts.dashboard.activities.title")}</div>
          <div className="sectionDesc">{t("delivery.accounts.dashboard.activities.desc")}</div>
        </div>
        <div className="flex-[2] justify-center">
          <ActivityDashboard analytics={accountData.delivery_analytics} />
        </div>
      </div>

      <div className={s.dashboardContent}>
        <div className="flex-[1]">
          <div className="settingTitle">{t("dashboard.messagesDetails.title")}</div>
          <div className="sectionDesc">{t("dashboard.messagesDetails.desc")}</div>
        </div>
        <div className="flex-[2]">
          {isLoadingDailyBreakdown ? (
            <div className="flex items-center justify-center w-full py-6">
              <LoaderIcon className="mt-6" color="var(--bg-d10)" />
            </div>
          ) : dailyBreakdown.resources.length === 0 ? (
            <div className="text-center text-sm pt-8 text-[--text-a60]">{t("infoMsg.noDataAvailableYet.msg")}</div>
          ) : (
            <>
              <ResponsiveTable
                headers={[
                  {
                    name: t("table.header.date"),
                  },
                  { name: t("table.header.processed") },
                ]}
                rows={dailyBreakdown.resources.slice(0, 6).map((d, idx) => {
                  return (
                    <tr key={idx}>
                      <td>{d.day}</td>
                      <td>{d.processed_count}</td>
                    </tr>
                  );
                })}
              />

              <div className="text-sm text-[--accent] text-right mt-2">
                <span
                  onClick={() => {
                    history.push(`${baseUrl}/delivery/accounts/${accountData.id}/analytics`);
                  }}
                  className="cursor-pointer"
                >
                  {t("button.expandTable")}
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      <div className={s.dashboardContent}>
        <div className="flex-[1]">
          <div className="settingTitle">{t("dashboard.messagesPerDomain.title")}</div>
          <div className="sectionDesc">{t("dashboard.messagesPerDomain.desc")}</div>
        </div>
        <div className="flex-[2]">
          {isLoadingDomainsRank ? (
            <div className="flex items-center justify-center w-full py-6">
              <LoaderIcon className="mt-6" color="var(--bg-d10)" />
            </div>
          ) : domainsRank.resources.length === 0 ? (
            <div className="text-center text-sm pt-8 text-[--text-a60]">{t("infoMsg.noDataAvailableYet.msg")}</div>
          ) : (
            <>
              <ResponsiveTable
                headers={[
                  {
                    name: t("table.header.domain"),
                  },
                  { name: t("table.header.processed") },
                ]}
                rows={domainsRank.resources.slice(0, 5).map((d, idx) => {
                  return (
                    <tr key={idx}>
                      <td>{d.domain}</td>
                      <td>{d.processed_count}</td>
                    </tr>
                  );
                })}
              />

              <div className="text-sm text-[--accent] text-right mt-2">
                <span
                  onClick={() =>
                    history.push(`${baseUrl}/delivery/accounts/${accountData.id}/analytics?type=domains-rank`)
                  }
                  className="cursor-pointer"
                >
                  {t("button.expandTable")}
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      <ConfirmModal
        title={t("teamMembers.forbiddenAction")}
        text={t("teamMembers.forbiddenActionSubtitle")}
        textButton="button.ok"
        hideCancelButton={true}
        opened={isForbiddenModalOpen}
        action={() => setIsForbiddenModalOpen(false)}
        handleClose={() => setIsForbiddenModalOpen(false)}
        buttonColor="accent"
      />
    </div>
  );
};

export default Dashboard;
